import React, { useState } from "react";
import { toast } from "sonner";
import { Building2, ArrowRight, CheckCircle2, MapPin, Eye, Save, HeartHandshake } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useFormDraft } from "../hooks/useFormDraft";
import { CLINIC_TYPES, AUDIENCES, AudienceChip, EstablishmentBadge } from "../lib/clinicMeta";

/**
 * Pré-cadastro público de Unidade Parceira (clínica).
 *
 * Submete uma CANDIDATURA (POST /api/applications, type="clinica") — não cria
 * conta nem loga o usuário. Só depois que o admin RAF aprova é que a clínica
 * recebe as credenciais e vê os termos comerciais dentro da área autenticada.
 *
 * Feb 2026 — Unificação FP: mesmo componente atende `/seja-parceiro` (origin=raf)
 * e `/filho-presente/cadastre-clinica` (origin=fp). O prop `origin="fp"` muda o
 * branding (âmbar Filho Presente + copy focada em idosos) e é enviado no payload
 * pra o admin marcar `serves_fp=True` no aprovar. Toda a lógica RAF é preservada.
 */
const ClinicApply = ({ origin = "raf" }) => {
  const isFp = origin === "fp";
  const brandName = isFp ? "Filho Presente" : "RAF Direct";
  const themeAccent = isFp ? "text-amber-600" : "text-emerald-700";
  const themeButton = isFp
    ? "bg-amber-500 hover:bg-amber-600"
    : "bg-emerald-700 hover:bg-emerald-800";
  const eyebrow = isFp
    ? "Seja clínica / ILPI parceira Filho Presente"
    : "Seja uma Unidade Parceira RAF";
  const heroTitle = isFp
    ? <>Cadastre sua <span className="text-amber-600">clínica, ILPI ou casa de repouso</span></>
    : <>Cadastre sua <span className="text-emerald-700">clínica ou unidade de tratamento</span></>;
  const heroP1 = isFp
    ? <>O <b>Filho Presente</b> coordena cuidados de idosos e <b>encaminha famílias qualificadas</b> para ILPIs, casas de repouso, centros-dia e clínicas geriátricas parceiras. Torne-se parceiro e receba acolhimento humano com triagem prévia.</>
    : <>A RAF Direct <b>encaminha famílias já triadas</b> para tratamento presencial em unidades parceiras — internação, hospital-dia, comunidade terapêutica. Diferente do profissional (que atende via videochamada), <b>na clínica o paciente e a família comparecem presencialmente</b>.</>;

  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    clinic_name: "", clinic_cnpj: "", clinic_city: "", clinic_state: "",
    establishment_type: "",
    audience: isFp ? ["Idosos"] : [],
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { clear } = useFormDraft(
    isFp ? "fp.clinic-apply.draft" : "raf.clinic-apply.draft",
    form, setForm,
  );
  const set = (k, v) => setForm({ ...form, [k]: v });
  const toggleAudience = (val) => setForm((f) => ({
    ...f,
    audience: f.audience.includes(val) ? f.audience.filter((x) => x !== val) : [...f.audience, val],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/applications", {
        type: "clinica",
        origin,
        name: form.clinic_name,
        email: form.email,
        phone: form.phone,
        message: `Responsável: ${form.name}${form.message ? "\n\n" + form.message : ""}`,
        clinic_data: {
          city: form.clinic_city,
          state: form.clinic_state,
          cnpj: form.clinic_cnpj,
          establishment_type: form.establishment_type,
          audience: form.audience,
        },
      });
      clear();
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar candidatura");
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="clinic-apply-success">
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 ${isFp ? "bg-amber-100" : "bg-emerald-100"}`}>
          <CheckCircle2 className={`w-9 h-9 ${isFp ? "text-amber-700" : "text-emerald-700"}`} />
        </div>
        <h1 className="font-display font-bold text-[#0B2B62] text-3xl md:text-4xl tracking-tight">
          Candidatura enviada com sucesso
        </h1>
        <p className="text-slate-600 mt-4 leading-relaxed">
          Recebemos os dados da sua unidade. Nossa equipe <b>{brandName}</b> analisará seu perfil
          em até <b>48 horas úteis</b>. Se aprovado, você receberá por e-mail e WhatsApp as
          credenciais de acesso, os termos comerciais completos e o próximo passo do cadastro.
        </p>
        <p className="text-slate-500 text-sm mt-4">
          Nenhuma conta foi criada ainda — a clínica só entra na vitrine pública depois da aprovação do admin.
        </p>
        <Link to={isFp ? "/filho-presente" : "/"} className="inline-flex items-center gap-2 mt-8 px-5 py-2.5 rounded-md border border-slate-300 text-sm text-slate-700 hover:bg-slate-50" data-testid="clinic-apply-back">
          Voltar para a home
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-16" data-testid="clinic-apply-page">
      <div className={`text-xs uppercase tracking-[0.2em] font-bold inline-flex items-center gap-1.5 ${isFp ? "text-amber-600" : "text-[#0B2B62]"}`}>
        {isFp ? <HeartHandshake className="w-3.5 h-3.5" /> : <Building2 className="w-3.5 h-3.5" />} {eyebrow}
      </div>
      <h1 className="font-display font-bold text-[#0B2B62] text-3xl md:text-4xl tracking-tight mt-2">
        {heroTitle}
      </h1>
      <p className="text-slate-600 mt-4 leading-relaxed text-left max-w-3xl">
        {heroP1}
      </p>
      <p className="text-slate-500 text-sm mt-2 text-left max-w-3xl">
        Envie sua candidatura abaixo. Após análise (até 48h úteis), você recebe as credenciais de acesso, lê os <b>termos comerciais completos</b> e conclui o cadastro na área autenticada. O cadastro é o <b>mesmo padrão RAF Direct</b> — mesmos campos, mesmos critérios, mesma vitrine anonimizada.
      </p>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_360px] gap-8 mt-10 items-start">
        <form onSubmit={submit} className="space-y-4 bg-white border border-slate-200 rounded-xl p-6" data-testid="clinic-apply-form">
          <div className="flex items-center justify-between border-b border-slate-100 pb-2">
            <div className="text-xs uppercase tracking-widest text-slate-500">Responsável pela candidatura</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 inline-flex items-center gap-1" title="Seus dados são salvos automaticamente neste navegador enquanto você preenche.">
              <Save className="w-3 h-3" /> rascunho salvo
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Seu nome completo *</label>
            <input required minLength={3} value={form.name} onChange={(e) => set("name", e.target.value)}
              data-testid="ca-name" className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 ${isFp ? "focus:ring-amber-500" : "focus:ring-emerald-500"}`} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">E-mail *</label>
              <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                data-testid="ca-email" className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 ${isFp ? "focus:ring-amber-500" : "focus:ring-emerald-500"}`} />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Telefone / WhatsApp *</label>
              <input required value={form.phone} onChange={(e) => set("phone", e.target.value)}
                data-testid="ca-phone" placeholder="(11) 99999-9999"
                className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 ${isFp ? "focus:ring-amber-500" : "focus:ring-emerald-500"}`} />
            </div>
          </div>

          <div className="text-xs uppercase tracking-widest text-slate-500 border-b border-slate-100 pb-2 pt-3">Dados da unidade</div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Nome/Razão Social da clínica *</label>
            <input required minLength={3} value={form.clinic_name} onChange={(e) => set("clinic_name", e.target.value)}
              data-testid="ca-clinic-name" className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 ${isFp ? "focus:ring-amber-500" : "focus:ring-emerald-500"}`} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">CNPJ</label>
              <input value={form.clinic_cnpj} onChange={(e) => set("clinic_cnpj", e.target.value)}
                data-testid="ca-cnpj" placeholder="00.000.000/0000-00"
                className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 ${isFp ? "focus:ring-amber-500" : "focus:ring-emerald-500"}`} />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Cidade *</label>
              <input required value={form.clinic_city} onChange={(e) => set("clinic_city", e.target.value)}
                data-testid="ca-city" className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 ${isFp ? "focus:ring-amber-500" : "focus:ring-emerald-500"}`} />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">UF *</label>
              <input required maxLength={2} value={form.clinic_state} onChange={(e) => set("clinic_state", e.target.value.toUpperCase())}
                data-testid="ca-state" placeholder="SP"
                className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 ${isFp ? "focus:ring-amber-500" : "focus:ring-emerald-500"}`} />
            </div>
          </div>

          {/* Tipo de estabelecimento */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Tipo de estabelecimento *</label>
            <p className="text-[11px] text-slate-500 mt-0.5">Como sua unidade se define — ajuda a família a te encontrar na vitrine.</p>
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="ca-establishment">
              {CLINIC_TYPES.filter((t) => t.value !== "Outros").map((t) => {
                const Icon = t.icon;
                const active = form.establishment_type === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => set("establishment_type", active ? "" : t.value)}
                    data-testid={`ca-est-${t.value.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border font-semibold transition-colors ${
                      active
                        ? "bg-[#0B2B62] border-[#0B2B62] text-white"
                        : `${t.bg} ${t.text} ${t.border} hover:brightness-95`
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Público */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Público atendido</label>
            <p className="text-[11px] text-slate-500 mt-0.5">Marque um ou mais — algumas unidades atendem só masculino/feminino, outras são mistas.</p>
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="ca-audience">
              {AUDIENCES.map((a) => {
                const Icon = a.icon;
                const active = form.audience.includes(a.value);
                return (
                  <button
                    key={a.value}
                    type="button"
                    onClick={() => toggleAudience(a.value)}
                    data-testid={`ca-aud-${a.value.toLowerCase()}`}
                    className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border font-semibold transition-colors ${
                      active
                        ? "bg-[#0B2B62] border-[#0B2B62] text-white"
                        : `${a.bg} ${a.text} ${a.border} hover:brightness-95`
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {a.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Mensagem para o admin (opcional)</label>
            <textarea rows={3} value={form.message} onChange={(e) => set("message", e.target.value)}
              data-testid="ca-message" placeholder="Modalidades, capacidade, diferenciais, equipe..."
              className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 ${isFp ? "focus:ring-amber-500" : "focus:ring-emerald-500"}`} />
          </div>

          <button type="submit" disabled={loading}
            data-testid="ca-submit"
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-md text-white font-bold text-sm disabled:opacity-50 ${themeButton}`}>
            {loading ? "Enviando…" : <>Enviar candidatura <ArrowRight className="w-4 h-4" /></>}
          </button>
          <p className="text-xs text-slate-500">
            O envio deste formulário <b>não cria conta</b>. A conta e o acesso são gerados só após aprovação do admin (até 48h úteis).
            {isFp && <> Sua unidade nasce cross-listada na RAF Direct também — máxima visibilidade desde o dia 1.</>}
          </p>
        </form>

        {/* Vitrine Preview — anonimizada como a família vê */}
        <aside className="lg:sticky lg:top-24" data-testid="clinic-vitrine-preview">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold inline-flex items-center gap-1 mb-3">
            <Eye className="w-3 h-3" /> Prévia da vitrine pública {isFp && <span className={themeAccent}>· Filho Presente</span>}
          </div>
          <div className="group bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <div className="aspect-[16/10] bg-slate-100 flex items-center justify-center text-slate-300 relative">
              <Building2 className="w-12 h-12" />
              {form.establishment_type && (
                <div className="absolute top-2 left-2">
                  <EstablishmentBadge type={form.establishment_type} size="sm" />
                </div>
              )}
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-mono uppercase tracking-widest bg-[#0B2B62] text-white px-1.5 py-0.5 rounded">P00X</span>
                <h3 className="font-display font-bold text-lg text-[#0B2B62]">Clínica P00X</h3>
              </div>
              <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                <MapPin className="w-3 h-3" />
                {form.clinic_city || "Cidade"}, {form.clinic_state || "UF"}
              </p>
              <p className="text-sm text-slate-600 mt-2 line-clamp-2">
                {form.message
                  ? form.message.slice(0, 140)
                  : (isFp ? "Unidade parceira Filho Presente — cuidado especializado do idoso." : "Unidade parceira credenciada da RAF Direct.")}
              </p>
              {form.audience.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {form.audience.slice(0, 4).map((a) => <AudienceChip key={a} audience={a} />)}
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-[#0B2B62] font-semibold inline-flex items-center gap-1">
                Ver detalhes <ArrowRight className="w-3 h-3" />
              </div>
            </div>
          </div>
          <div className="mt-3 text-[11px] text-slate-500 leading-relaxed">
            <b>Vitrine anonimizada</b> — as famílias veem só o código, cidade/UF, tipo de
            estabelecimento, público atendido e o selo de certificação. O nome real
            (<i>{form.clinic_name || "sua unidade"}</i>) e os contatos só são revelados
            pelo consultor durante o acolhimento, protegendo famílias e unidade.
          </div>
        </aside>
      </div>
    </div>
  );
};

export default ClinicApply;
